import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Copy, X, CheckCircle, UserPlus } from '../icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export interface InviteRespondentModalProps {
  assessmentCode: string;
  coachName: string;
  onClose: () => void;
  isFreeProfile?: boolean;
  locale?: 'en' | 'tr';
}

export function InviteRespondentModal({
  assessmentCode,
  coachName,
  onClose,
  isFreeProfile = false,
  locale = 'en',
}: InviteRespondentModalProps) {
  const { user } = useAuth();
  const registrationLink = `https://my.nlpos.app/register?code=${assessmentCode}`;
  const [copied, setCopied] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const t = {
    inviteSomeone: locale === 'tr' ? 'Biri Davet Et' : 'Invite Someone',
    subtitle: locale === 'tr'
      ? 'Benzersiz kayit baglantinizi paylasin. Kisi kendi hesabini olusturur, kendi sifresini belirler ve Profili tamamlar.'
      : 'Share your unique registration link. The person creates their own account, chooses their own password, and completes the Profile.',
    regLink: locale === 'tr' ? 'Kayit Baglantisi' : 'Registration Link',
    copy: locale === 'tr' ? 'Kopyala' : 'Copy',
    copied: locale === 'tr' ? 'Kopyalandi' : 'Copied',
    orSend: locale === 'tr' ? 'veya kayit baglantisini e-posta ile gonderin' : 'or email the registration link directly',
    firstName: locale === 'tr' ? 'Ad' : 'First Name',
    lastName: locale === 'tr' ? 'Soyad' : 'Last Name',
    emailLabel: locale === 'tr' ? 'E-posta' : 'Email',
    sendInvitation: locale === 'tr' ? 'Davet Gonder' : 'Send Invitation',
    sending: locale === 'tr' ? 'Gonderiliyor...' : 'Sending...',
    sentTo: locale === 'tr' ? 'Davet gonderildi' : 'Invitation sent to',
    emailSent: locale === 'tr'
      ? `${email} adresine kayit baglantisi gonderildi. Kisi baglantiyi acarak kendi hesabini ve sifresini olusturabilir.`
      : `The registration link was sent to ${email}. They can use it to create their own account and password.`, 
    shareHint: locale === 'tr'
      ? 'Bu baglantiyi WhatsApp, SMS veya sosyal medya uzerinden de paylasabilirsiniz.'
      : 'You can also copy this link to share via WhatsApp, SMS, or social.',
    spamHint: locale === 'tr'
      ? 'Davet edilen kisinin e-postayi spam/ontem kutusunda kontrol etmesini isteyin.'
      : 'Ask the person you invited to check their spam or junk folder if they do not see the email.',
    error: locale === 'tr'
      ? 'E-posta gonderilemedi. Lutfen tekrar deneyin veya baglantiyi kopyalayin.'
      : 'The email could not be sent. Please try again or copy the link instead.',
  };

  const copyLink = () => {
    navigator.clipboard.writeText(registrationLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !email) return;
    setIsSending(true);
    setSendError(null);
    try {
      if (!user?.id) {
        setSendError(locale === 'tr'
          ? 'Oturumunuz sona erdi. Lutfen tekrar giris yapin.'
          : 'Your session has expired. Please sign out and sign back in, then try again.');
        return;
      }

      const { data: coach, error: coachError } = await supabase
        .from('coaches')
        .select('id, free_profile_first_invitation_date, account_plan')
        .eq('user_id', user.id)
        .maybeSingle();

      if (coachError) throw new Error(coachError.message);

      if (!coach) {
        setSendError(locale === 'tr'
          ? 'Koc hesabiniz bulunamadi. Lutfen tekrar giris yapin.'
          : 'Your coach account could not be found. Please sign out and sign back in, then try again.');
        return;
      }

      if (isFreeProfile && !coach.free_profile_first_invitation_date) {
        await supabase.from('coaches').update({
          free_profile_first_invitation_date: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }).eq('id', coach.id);

        await supabase.from('free_profile_funnel_events').insert({
          coach_id: coach.id,
          event_type: 'first_invitation_sent',
          event_metadata: { respondent_email: email, respondent_name: `${firstName} ${lastName}` },
        });

        const { error: coachEmailError } = await supabase.functions.invoke('send-email', {
          body: {
            type: 'free_profile_invitation_sent',
            recipientEmail: user.email || '',
            recipientName: coachName,
            respondentName: `${firstName} ${lastName}`.trim(),
            coachId: coach.id,
          },
        });
        if (coachEmailError) throw new Error(coachEmailError.message || 'Email service error');
      }

      const emailType = isFreeProfile
        ? 'free_profile_respondent_invitation'
        : 'respondent_invitation';

      const { error: respondentEmailError } = await supabase.functions.invoke('send-email', {
        body: {
          type: emailType,
          recipientEmail: email,
          recipientName: `${firstName} ${lastName}`.trim(),
          coachName,
          registrationLink,
          assessmentCode,
          coachId: coach.id,
        },
      });
      if (respondentEmailError) throw new Error(respondentEmailError.message || 'Email service error');

      if (isFreeProfile) {
        await supabase.from('free_profile_pending_invitations').upsert({
          coach_id: coach.id,
          email,
          first_name: firstName,
          last_name: lastName || null,
          invited_at: new Date().toISOString(),
        }, { onConflict: 'coach_id,email' });
      }

      setSent(true);
    } catch (err: any) {
      const rawError = err?.message || '';
      let friendlyError = t.error;

      if (rawError.includes('Missing required fields')) {
        friendlyError = locale === 'tr'
          ? 'Eksik bilgi. Lutfen ad ve e-posta alanlarini doldurun.'
          : 'Some required information is missing. Please fill in the first name and email fields.';
      } else if (rawError.includes('registrationLink required')) {
        friendlyError = locale === 'tr'
          ? 'Kayit baglantisi eksik. Lutfen sayfayi yenileyin ve tekrar deneyin.'
          : 'Your registration link is missing. Please refresh the page and try again.';
      } else if (rawError.includes('Coach email configuration')) {
        friendlyError = locale === 'tr'
          ? 'E-posta yapilandirmasi sorunu. Lutfen yonetici ile iletisime gecin.'
          : 'There is a problem with the email configuration. Please contact support.';
      } else if (rawError.includes('Failed to send email')) {
        friendlyError = locale === 'tr'
          ? 'E-posta servizi gonderimi reddetti. Lutfen tekrar deneyin veya baglantiyi kopyalayin.'
          : 'The email service rejected the send request. Please try again, or copy the link and share it directly.';
      } else if (rawError.includes('Internal server error') || rawError.includes('fetch')) {
        friendlyError = locale === 'tr'
          ? 'Sunucu hatasi. Lutfen biraz bekleyip tekrar deneyin.'
          : 'The server encountered an error. Please wait a moment and try again.';
      } else if (rawError) {
        friendlyError = rawError;
      }

      setSendError(friendlyError);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="freud-card-elevated p-6 w-full max-w-lg relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg text-chocolate-400 hover:bg-cream-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-sage-100 rounded-lg">
            <UserPlus className="w-5 h-5 text-sage-600" />
          </div>
          <h2 className="freud-heading-lg text-chocolate-900">{t.inviteSomeone}</h2>
        </div>
        <p className="freud-text-sm text-chocolate-500 mb-6">{t.subtitle}</p>

        {/* Registration link */}
        <div className="mb-6">
          <label className="freud-label block text-chocolate-700 mb-2">{t.regLink}</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-cream-100 rounded-xl px-4 py-3">
              <p className="freud-text-xs text-chocolate-600 font-mono break-all">{registrationLink}</p>
            </div>
            <button
              onClick={copyLink}
              className="freud-btn-primary flex items-center gap-2 flex-shrink-0"
            >
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? t.copied : t.copy}
            </button>
          </div>
          <p className="freud-text-xs text-chocolate-400 mt-2">{t.shareHint}</p>
        </div>

        <div className="relative flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-chocolate-100" />
          <span className="freud-text-xs text-chocolate-400">{t.orSend}</span>
          <div className="flex-1 h-px bg-chocolate-100" />
        </div>

        {sent ? (
          <div className="flex items-center gap-3 bg-sage-50 border border-sage-200 rounded-xl p-4">
            <CheckCircle className="w-5 h-5 text-sage-600 flex-shrink-0" />
            <div>
              <p className="freud-text-sm font-medium text-sage-700">{t.sentTo} {firstName}</p>
              <p className="freud-text-xs text-sage-600">{t.emailSent}</p>
              <p className="freud-text-xs text-sage-500 mt-1">{t.spamHint}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={sendInvitation} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="freud-label block text-chocolate-700 mb-1">{t.firstName}</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="freud-input"
                  placeholder="Jane"
                />
              </div>
              <div>
                <label className="freud-label block text-chocolate-700 mb-1">{t.lastName}</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="freud-input"
                  placeholder="Smith"
                />
              </div>
            </div>
            <div>
              <label className="freud-label block text-chocolate-700 mb-1">{t.emailLabel}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="freud-input"
                placeholder="jane@example.com"
              />
            </div>
            {sendError && (
              <p className="freud-text-xs text-warm-600">{sendError}</p>
            )}
            <button
              type="submit"
              disabled={isSending}
              className="freud-btn-primary w-full flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isSending ? t.sending : t.sendInvitation}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

export function ShareLinkCard({ assessmentCode, locale = 'en' }: { assessmentCode: string; locale?: 'en' | 'tr' }) {
  const [copied, setCopied] = useState(false);
  const registrationLink = `https://my.nlpos.app/register?code=${assessmentCode}`;

  const copyLink = () => {
    navigator.clipboard.writeText(registrationLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const t = {
    title: locale === 'tr' ? 'Profil Kayit Baglantisi' : 'Profile Registration Link',
    subtitle: locale === 'tr'
      ? 'Bu baglantiyi paylasin. Assessment kodu otomatik olarak doldurulur.'
      : 'Share this link. The assessment code is auto-filled on the registration page.',
    copy: locale === 'tr' ? 'Kopyala' : 'Copy',
    copied: locale === 'tr' ? 'Kopyalandi' : 'Copied',
  };

  return (
    <div className="freud-card-elevated p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2.5 bg-sage-100 rounded-xl">
          <UserPlus className="w-5 h-5 text-sage-600" />
        </div>
        <div>
          <h3 className="freud-heading-base text-chocolate-900">{t.title}</h3>
          <p className="freud-text-xs text-chocolate-500">{t.subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-cream-100 rounded-xl px-4 py-2.5">
          <p className="freud-text-xs text-chocolate-600 font-mono break-all">{registrationLink}</p>
        </div>
        <button
          onClick={copyLink}
          className="freud-btn-primary flex items-center gap-2 flex-shrink-0"
        >
          {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? t.copied : t.copy}
        </button>
      </div>
    </div>
  );
}
